// src/pages/CaseValueEstimatorPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import DocLanguageSelect from "../components/DocLanguageSelect";
import { generateCaseValueEstimate } from "../utils/valueEstimateApiClient"; // implement like your cost client
import { useAuth } from "../context/AuthContext";

/** =========================
 *  Small-screen detector
 *  ========================= */
function useIsSmallScreen(breakpoint = 640) {
  const getInitial = () =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false;
  const [isSmall, setIsSmall] = useState(getInitial());
  useEffect(() => {
    const onResize = () => setIsSmall(getInitial());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line
  }, []);
  return isSmall;
}

/** =========================
 *  Tooltip Helpers (mobile-safe, clamped to FORM width)
 *  ========================= */
const isTouchDevice = () =>
  typeof window !== "undefined" &&
  ("ontouchstart" in window || (navigator && navigator.maxTouchPoints > 0));

const HelpIcon = ({ onClick, onMouseEnter, onMouseLeave, label }) => (
  <button
    type="button"
    aria-label={"Help: " + label}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 20,
      height: 20,
      borderRadius: 6,
      border: "1px solid #c7d2fe",
      background: "#eef2ff",
      color: "#4338ca",
      cursor: "pointer",
      fontSize: 12,
      lineHeight: 1,
      marginLeft: 6,
      position: "relative",
      userSelect: "none",
    }}
  >
    ?
  </button>
);

const HelpBubble = ({ show, text, pos }) =>
  show ? (
    <div
      role="tooltip"
      style={{
        position: "fixed",
        zIndex: 9999,
        top: pos.top,
        left: pos.left,
        maxWidth: pos.maxWidth, // clamp to form width
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid #e5e7eb",
        background: "#ffffff",
        boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
        fontSize: 12,
        color: "#374151",
        lineHeight: 1.45,
        wordWrap: "break-word",
        whiteSpace: "normal",
      }}
    >
      {text}
    </div>
  ) : null;

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

const HelpTip = ({ label, text }) => {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, maxWidth: "calc(100vw - 24px)" });

  // Compute a clamped position for the bubble INSIDE the form container
  const positionBubble = () => {
    if (!rootRef.current || typeof window === "undefined") return;

    const rect = rootRef.current.getBoundingClientRect();
    const formEl = rootRef.current.closest("[data-form-root='true']");
    const containerRect = formEl
      ? formEl.getBoundingClientRect()
      : { left: 12, right: window.innerWidth - 12 };
    const containerWidth = containerRect.right - containerRect.left;

    const bubbleWidth = 260;
    const margin = 8;
    const top = rect.bottom + margin;

    const minLeft = containerRect.left + margin;
    const maxLeft = containerRect.right - bubbleWidth - margin;

    const left = clamp(
      rect.left - bubbleWidth / 2 + rect.width / 2,
      minLeft,
      Math.max(minLeft, maxLeft)
    );

    setPos({ top, left, maxWidth: containerWidth - margin * 2 + "px" });
  };

  useEffect(() => {
    if (!open) return;
    positionBubble();
    const closeOnOutside = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    const handleScroll = () => positionBubble();
    const handleResize = () => positionBubble();

    document.addEventListener("mousedown", closeOnOutside, true);
    document.addEventListener("touchstart", closeOnOutside, true);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", closeOnOutside, true);
      document.removeEventListener("touchstart", closeOnOutside, true);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line
  }, [open]);

  const touchMode = isTouchDevice();

  const handleClick = () => setOpen((v) => !v);
  const handleEnter = touchMode ? undefined : () => setOpen(true);
  const handleLeave = touchMode ? undefined : () => setOpen(false);

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block" }}>
      <HelpIcon
        label={label}
        onClick={handleClick}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      />
      <HelpBubble show={open} text={text} pos={pos} />
    </div>
  );
};

/** =========================
 *  UI HELPERS (consistent widths)
 *  ========================= */
const LABEL_W = 210;
const FIELD_W = 280; // unified visual width fallback

const SectionTitle = ({ children }) => (
  <h3 style={{ margin: "20px 0 10px", fontSize: 18, fontWeight: 700 }}>{children}</h3>
);
const Row = ({ children, wrap }) => (
  <div
    style={{
      display: "flex",
      flexWrap: wrap ? "wrap" : "nowrap",
      gap: 12,
      alignItems: "center",
      margin: "10px 0",
      overflow: "visible",
      position: "relative",
    }}
  >
    {children}
  </div>
);
const FieldLabel = ({ id, children, help, required, w = LABEL_W }) => (
  <div style={{ position: "relative", width: w, fontWeight: 600, lineHeight: 1.2 }}>
    <label htmlFor={id} style={{ fontWeight: 600, display: "block", paddingRight: 24 }}>
      {children}
      {required ? "\u00A0*" : ""}
    </label>
    {help ? (
      <div style={{ position: "absolute", top: 0, right: 0 }}>
        <HelpTip label={children} text={help} />
      </div>
    ) : null}
  </div>
);

const Select = ({ style, ...props }) => {
  const { width, ...styleRest } = style || {};
  return (
    <select
      {...props}
      style={{
        width: "var(--field-w, " + FIELD_W + "px)",
        padding: "10px 12px",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        outline: "none",
        background: "#fff",
        position: "relative",
        appearance: "auto",
        boxSizing: "border-box",
        ...styleRest,
      }}
    />
  );
};

const Input = ({ style, ...props }) => {
  const { width, ...styleRest } = style || {}; // strip any incoming width
  return (
    <input
      {...props}
      style={{
        width: "var(--field-w, " + FIELD_W + "px)",
        padding: "10px 12px",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        outline: "none",
        position: "relative",
        boxSizing: "border-box",
        ...styleRest,
      }}
    />
  );
};

// Textarea styled like DocDynamicFieldsForm (unchanged)
const Textarea = (props) => (
  <textarea
    {...props}
    style={{
      width: "100%",
      maxWidth: "100%",
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      background: "#fff",
      minHeight: 160,
      resize: "vertical",
      boxSizing: "border-box",
      whiteSpace: "pre-wrap",
      overflowWrap: "break-word",
      ...props.style,
    }}
  />
);
const Help = ({ children }) => (
  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6, lineHeight: 1.4 }}>{children}</div>
);

/** =========================
 *  OPTION SETS
 *  ========================= */
const CASE_TYPES = [
  "Family Law",
  "Immigration",
  "Criminal Defense",
  "Civil Litigation",
  "Business/Corporate",
  "Personal Injury",
  "Real Estate",
  "Employment",
  "Estate/Probate",
  "Bankruptcy",
  "IP & Tech",
  "Tax",
  "Administrative/Gov",
  "Contracts & Commercial",
  "Housing/Landlord-Tenant",
  "Consumer & Privacy",
];

const SUBTYPES_BY_CASE = {
  "Family Law": [
    "Uncontested Divorce",
    "Contested Divorce",
    "Custody",
    "Support/Modification",
    "Prenup/Postnup",
    "Adoption",
    "Domestic Violence",
    "Guardianship",
    "Paternity",
    "Property Division",
  ],
  Immigration: [
    "Family Petition (I-130)",
    "Asylum",
    "Removal Defense",
    "Employment Visa",
    "Adjustment of Status",
    "Naturalization (N-400)",
    "RFE/NOID Response",
    "Waivers",
    "TPS/DACA",
  ],
  "Criminal Defense": [
    "Infraction",
    "Misdemeanor",
    "Felony",
    "DUI/DWI",
    "White Collar",
    "Juvenile",
    "Expungement/Record Relief",
    "Appeal",
  ],
  "Civil Litigation": [
    "Breach of Contract",
    "Business Tort",
    "Negligence",
    "Defamation",
    "Small Claims",
    "Class Action (plaintiff)",
    "Class Action (defense)",
    "Appeal",
  ],
  "Business/Corporate": [
    "LLC/Corp Formation",
    "Bylaws/Operating Agreement",
    "Commercial Contracts",
    "Compliance",
    "M&A",
    "Securities/Financing",
    "Corporate Governance",
    "Franchise",
  ],
  "Personal Injury": [
    "Auto Accident",
    "Premises Liability",
    "Medical Malpractice",
    "Product Liability",
    "Wrongful Death",
    "Workplace Injury (non-comp)",
    "Catastrophic Injury",
  ],
  "Real Estate": [
    "Residential Purchase/Sale",
    "Commercial Purchase/Sale",
    "Lease (Landlord)",
    "Lease (Tenant)",
    "Eviction/Unlawful Detainer",
    "Title/Boundary Dispute",
    "HOA/Condo",
    "Land Use/Zoning",
  ],
  Employment: [
    "Wage/Hour",
    "Discrimination",
    "Harassment",
    "Retaliation",
    "Wrongful Termination",
    "Non-Compete/Trade Secrets",
    "Employee Handbook/Policy",
    "Severance/Negotiation",
  ],
  "Estate/Probate": [
    "Will/Trust",
    "Probate Administration",
    "Conservatorship",
    "Guardianship (minor)",
    "Trust Litigation",
    "Estate Planning (tax-focused)",
    "POA/Healthcare Directive",
  ],
  Bankruptcy: [
    "Chapter 7",
    "Chapter 13",
    "Chapter 11 (small business)",
    "Adversary",
    "Creditor Defense",
    "Debt Settlement",
  ],
  "IP & Tech": [
    "Trademark (filing)",
    "Trademark Dispute",
    "Copyright",
    "Patent (provisional)",
    "Licensing",
    "DMCA/Platform",
    "Software/Privacy/Terms",
  ],
  Tax: [
    "Audit/Examination",
    "Collections/Installment",
    "Offer in Compromise",
    "Appeals",
    "SALT",
    "International/FBAR",
    "Entity Structuring (tax)",
  ],
  "Administrative/Gov": [
    "Professional License",
    "Benefits/Entitlements",
    "Agency Appeal",
    "Public Records/Open Meetings",
    "Regulatory Compliance",
  ],
  "Contracts & Commercial": [
    "Sales/Services Agreement",
    "NDA",
    "Licensing",
    "SaaS/Subscription",
    "Terms of Use/Privacy Policy",
    "MSA",
    "SOW",
  ],
  "Housing/Landlord-Tenant": [
    "Security Deposit",
    "Habitability/Repairs",
    "Rent Increase/Control",
    "Subsidized Housing",
    "Fair Housing",
    "Eviction Defense",
  ],
  "Consumer & Privacy": [
    "Debt Collection/FDCPA",
    "Credit Reporting/FCRA",
    "Data Breach/Notice",
    "Privacy Compliance (GDPR/CCPA)",
    "Consumer Arbitration",
  ],
};

const FORUMS = ["Local", "State", "Federal", "Immigration", "Administrative"];
const YES_NO_UNK = ["Yes", "No", "Unknown"];
const DEF_TYPES = ["Individual", "Business", "Government"];
const REP_STATUS = ["No Attorney", "Yes—Staying with Attorney", "Yes—Considering Switch"];
const NON_ECON_SCALE = ["Low", "Moderate", "High", "Severe"];
const EVIDENCE_SCALE = ["Very Weak", "Weak", "Moderate", "Strong", "Very Strong"];

/** =========================
 *  LOCAL STORAGE KEYS
 *  ========================= */
const LS_KEYS = {
  lastPayload: "cve:lastPayload",
  lastResult: "cve:lastResult",
  history: "cve:history",
  uses: "cve:uses",
  firstTs: "cve:firstTs",
};

/** =========================
 *  UTILITIES
 *  ========================= */
const clampLen = (s, max) => (s || "").slice(0, max); // no normalization/trim
const currency = (n) =>
  (Number.isFinite(n) ? n : 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

/** =========================
 *  QUOTA: 3 runs / 7 days (client-side; bypass if logged in)
 *  ========================= */
function checkQuotaAndBump({ bypass = false } = {}) {
  if (bypass) {
    return { ok: true, remaining: Infinity, resetAt: null, bypassed: true };
  }
  const now = Date.now();
  const windowMs = 7 * 24 * 60 * 60 * 1000;

  const usesRaw = localStorage.getItem(LS_KEYS.uses);
  const firstTsRaw = localStorage.getItem(LS_KEYS.firstTs);

  let uses = parseInt(usesRaw || "0", 10);
  let firstTs = firstTsRaw ? new Date(firstTsRaw).getTime() : null;

  if (!firstTs || now - firstTs > windowMs) {
    firstTs = now;
    uses = 0;
  }

  if (uses >= 3) {
    return { ok: false, remaining: 0, resetAt: new Date(firstTs + windowMs).toISOString() };
  }

  uses += 1;
  localStorage.setItem(LS_KEYS.uses, String(uses));
  localStorage.setItem(LS_KEYS.firstTs, new Date(firstTs).toISOString());

  return { ok: true, remaining: 3 - uses, resetAt: new Date(firstTs + windowMs).toISOString() };
}

/** =========================
 *  MAIN COMPONENT
 *  ========================= */
export default function CaseValueEstimatorPage() {
  const isSmall = useIsSmallScreen(700); // match content width
  const { currentUser } = useAuth();
  const loggedIn = !!currentUser;

  const [language, setLanguage] = useState("auto");

  // ---- Inputs ----
  // Core
  const [case_type, setCaseType] = useState("");
  const [case_subtype, setCaseSubtype] = useState("");
  const [forum, setForum] = useState("");
  const [rep_status, setRepStatus] = useState("");
  const [incident_date, setIncidentDate] = useState(""); // ISO date
  const [liability_admitted, setLiabilityAdmitted] = useState("Unknown");
  const [comparative_fault_pct, setComparativeFaultPct] = useState(""); // 0-100
  const [defendant_type, setDefendantType] = useState("");
  const [defendant_count, setDefendantCount] = useState("");
  const [insurance_known, setInsuranceKnown] = useState("Unknown");
  const [policy_limit, setPolicyLimit] = useState(""); // $
  const [prior_offer, setPriorOffer] = useState(""); // $
  const [non_econ_severity, setNonEconSeverity] = useState("");
  const [evidence_strength, setEvidenceStrength] = useState("");

  // Location (now required)
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [country, setCountry] = useState("");
  const [postal_code, setPostalCode] = useState("");

  // Advanced (damages & flags)
  const [medical_bills_to_date, setMedicalBillsToDate] = useState(""); // $
  const [property_damage, setPropertyDamage] = useState(""); // $
  const [wage_loss_to_date, setWageLossToDate] = useState(""); // $
  const [future_medical_estimate, setFutureMedicalEstimate] = useState(""); // $
  const [future_wage_loss, setFutureWageLoss] = useState(""); // $
  const [punitive_eligible, setPunitiveEligible] = useState("Unknown");
  const [public_entity_defendant, setPublicEntityDefendant] = useState("No");

  // Free text
  const [key_details, setKeyDetails] = useState("");

  // Consents
  const [acceptTerms, setAcceptTerms] = useState(false);

  // UI state
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const resultRef = useRef(null);

  // Restore last payload on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(LS_KEYS.lastPayload);
      if (saved) {
        const p = JSON.parse(saved);
        setLanguage(p.language ?? "auto");
        setCaseType(p.case_type ?? "");
        setCaseSubtype(p.case_subtype ?? "");
        setForum(p.forum ?? "");
        setRepStatus(p.rep_status ?? "");
        setIncidentDate(p.incident_date ?? "");
        setLiabilityAdmitted(p.liability_admitted ?? "Unknown");
        setComparativeFaultPct((p.comparative_fault_pct ?? "").toString());
        setDefendantType(p.defendant_type ?? "");
        setDefendantCount((p.defendant_count ?? "").toString());
        setInsuranceKnown(p.insurance_known ?? "Unknown");
        setPolicyLimit((p.policy_limit ?? "").toString());
        setPriorOffer((p.prior_offer ?? "").toString());
        setNonEconSeverity(p.non_econ_severity ?? "");
        setEvidenceStrength(p.evidence_strength ?? "");
        setCity(p.city ?? "");
        setCounty(p.county ?? "");
        setStateVal(p.state ?? "");
        setCountry(p.country ?? "");
        setPostalCode((p.postal_code ?? "").toString());
        setMedicalBillsToDate((p.medical_bills_to_date ?? "").toString());
        setPropertyDamage((p.property_damage ?? "").toString());
        setWageLossToDate((p.wage_loss_to_date ?? "").toString());
        setFutureMedicalEstimate((p.future_medical_estimate ?? "").toString());
        setFutureWageLoss((p.future_wage_loss ?? "").toString());
        setPunitiveEligible(p.punitive_eligible ?? "Unknown");
        setPublicEntityDefendant(p.public_entity_defendant ?? "No");
        setKeyDetails(p.key_details ?? "");
        setAcceptTerms(!!p.acceptTerms);
      }
      const savedResult = localStorage.getItem(LS_KEYS.lastResult);
      if (savedResult) setResult(JSON.parse(savedResult));
    } catch {}
  }, []);

  // Derived options for subtypes
  const subtypeOptions = useMemo(() => SUBTYPES_BY_CASE[case_type] || [], [case_type]);

  // Validate core (Location now required)
  const coreValid =
    case_type &&
    forum &&
    rep_status &&
    incident_date &&
    city &&
    county &&
    stateVal &&
    country &&
    postal_code &&
    acceptTerms;

  // Assemble payload
  const buildPayload = () => {
    const p = {
      language,
      // Core
      case_type,
      case_subtype,
      forum,
      rep_status,
      incident_date,
      liability_admitted,
      comparative_fault_pct: clampLen(String(comparative_fault_pct || ""), 3),
      defendant_type,
      defendant_count: clampLen(String(defendant_count || ""), 3),
      insurance_known,
      policy_limit: clampLen(String(policy_limit || ""), 12),
      prior_offer: clampLen(String(prior_offer || ""), 12),
      non_econ_severity,
      evidence_strength,

      // Location (required)
      city: clampLen(city, 80),
      county: clampLen(county, 80),
      state: clampLen(stateVal, 40),
      country: clampLen(country, 80),
      postal_code: clampLen(String(postal_code || ""), 12),

      // Advanced (optional)
      medical_bills_to_date: clampLen(String(medical_bills_to_date || ""), 12),
      property_damage: clampLen(String(property_damage || ""), 12),
      wage_loss_to_date: clampLen(String(wage_loss_to_date || ""), 12),
      future_medical_estimate: clampLen(String(future_medical_estimate || ""), 12),
      future_wage_loss: clampLen(String(future_wage_loss || ""), 12),
      punitive_eligible,
      public_entity_defendant,

      // Free text (1000, no normalization)
      key_details: clampLen(key_details, 1000),

      // Consents (persist locally)
      acceptTerms: !!acceptTerms,
    };
    return p;
  };

  // Save payload/result to localStorage (+ history of last 3)
  const persist = (payload, resultObj) => {
    try {
      localStorage.setItem(LS_KEYS.lastPayload, JSON.stringify(payload));
      if (resultObj) localStorage.setItem(LS_KEYS.lastResult, JSON.stringify(resultObj));
      const hist = JSON.parse(localStorage.getItem(LS_KEYS.history) || "[]");
      const next = [{ ts: new Date().toISOString(), payload, result: resultObj }, ...hist].slice(0, 3);
      localStorage.setItem(LS_KEYS.history, JSON.stringify(next));
    } catch {}
  };

  // Generate handler (calls backend; captcha handled in API client/back-end)
  const handleGenerate = async () => {
    setError("");

    if (!coreValid) {
      setError("Please complete the required fields (including Location) and accept the Terms & Privacy.");
      setResult(null);
      return;
    }

    // Quota gate (bypass if logged in)
    const quota = checkQuotaAndBump({ bypass: loggedIn });
    if (!quota.ok) {
      setError("You’ve reached 3 free estimates in the last 7 days. Please create a free account to continue.");
      setResult(null);
      return;
    }

    const payload = buildPayload();
    persist(payload, null); // save inputs immediately

    try {
      setIsGenerating(true);
      const data = await generateCaseValueEstimate(payload);
      setResult(data);
      persist(payload, data);

      // Scroll to result
      requestAnimationFrame(() => {
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (e) {
      setResult(null);
      setError(e?.message || "Failed to generate estimate.");
    } finally {
      setIsGenerating(false);
    }
  };

  // Clear all
  const handleReset = () => {
    setCaseType("");
    setCaseSubtype("");
    setForum("");
    setRepStatus("");
    setIncidentDate("");
    setLiabilityAdmitted("Unknown");
    setComparativeFaultPct("");
    setDefendantType("");
    setDefendantCount("");
    setInsuranceKnown("Unknown");
    setPolicyLimit("");
    setPriorOffer("");
    setNonEconSeverity("");
    setEvidenceStrength("");
    setCity("");
    setCounty("");
    setStateVal("");
    setCountry("");
    setPostalCode("");
    setMedicalBillsToDate("");
    setPropertyDamage("");
    setWageLossToDate("");
    setFutureMedicalEstimate("");
    setFutureWageLoss("");
    setPunitiveEligible("Unknown");
    setPublicEntityDefendant("No");
    setKeyDetails("");
    setAcceptTerms(false);
    setResult(null);
    setError("");
    // keep language as-is
  };

  /** =========================
   *  Render
   *  ========================= */
  return (
    <div
      data-form-root="true"
      style={{
        maxWidth: "700px",
        margin: "24px auto",
        padding: "24px",
        border: "1px solid #ccc",
        borderRadius: "10px",
        background: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        overflow: "visible",
        /** SURGICAL FIX: single source of truth for field width */
        "--field-w": "280px",
      }}
    >
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Case Value Estimator</h2>
      </div>

      {/* Core */}
      <SectionTitle>
        Core Details <span style={{ fontWeight: 400, color: "#6b7280" }}>(required)</span>
      </SectionTitle>

      <Row wrap>
        <FieldLabel id="caseType" required help="Select the legal area that best fits your matter.">
          Case Type
        </FieldLabel>
        <Select
          id="caseType"
          value={case_type}
          onChange={(e) => {
            setCaseType(e.target.value);
            setCaseSubtype("");
          }}
        >
          <option value="">Select…</option>
          {CASE_TYPES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </Row>

      <Row wrap>
        <FieldLabel id="caseSubtype" help="Optional. Narrower category within your case type.">
          Case Sub-type
        </FieldLabel>
        <Select id="caseSubtype" value={case_subtype} onChange={(e) => setCaseSubtype(e.target.value)}>
          <option value="">Select…</option>
          {subtypeOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
          <option value="Other">Other (specify in Key Facts)</option>
        </Select>
      </Row>

      <Row wrap>
        <FieldLabel id="forum" required help="Which forum applies (Local, State, Federal, Immigration, Administrative).">
          Forum/Court
        </FieldLabel>
        <Select id="forum" value={forum} onChange={(e) => setForum(e.target.value)}>
          <option value="">Select…</option>
          {FORUMS.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </Select>
      </Row>

      <Row wrap>
        <FieldLabel
          id="repStatus"
          required
          help="We won’t interfere with existing representation. Estimates are educational and not legal advice."
        >
          Representation Status
        </FieldLabel>
        <Select id="repStatus" value={rep_status} onChange={(e) => setRepStatus(e.target.value)}>
          <option value="">Select…</option>
          {REP_STATUS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
      </Row>

      <Row wrap>
        <FieldLabel id="incidentDate" required help="Incident/violation date helps check limitation periods and valuation windows.">
          Incident Date
        </FieldLabel>
        <Input
          id="incidentDate"
          type="date"
          value={incident_date}
          onChange={(e) => setIncidentDate(e.target.value)}
          style={{ width: FIELD_W }}
        />
      </Row>

      <Row wrap>
        <FieldLabel id="liabilityAdmitted" help="Has the other side admitted fault?">
          Liability Admitted
        </FieldLabel>
        <Select id="liabilityAdmitted" value={liability_admitted} onChange={(e) => setLiabilityAdmitted(e.target.value)}>
          {YES_NO_UNK.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </Select>
      </Row>

      <Row wrap>
        <FieldLabel id="faultPct" help="Your estimated share of fault (0–100). This reduces recoverable damages in many jurisdictions.">
          Comparative Fault (%)
        </FieldLabel>
        <Input
          id="faultPct"
          type="number"
          min="0"
          max="100"
          step="1"
          placeholder="e.g., 0"
          value={comparative_fault_pct}
          onChange={(e) => setComparativeFaultPct(e.target.value)}
          style={{ width: FIELD_W }}
        />
      </Row>

      <Row wrap>
        <FieldLabel id="defType" help="Who are you claiming against?">
          Defendant Type
        </FieldLabel>
        <Select id="defType" value={defendant_type} onChange={(e) => setDefendantType(e.target.value)}>
          <option value="">Select…</option>
          {DEF_TYPES.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </Select>
      </Row>

      <Row wrap>
        <FieldLabel id="defCount" help="How many defendants?">
          Defendant Count
        </FieldLabel>
        <Input
          id="defCount"
          type="number"
          min="1"
          step="1"
          placeholder="e.g., 1"
          value={defendant_count}
          onChange={(e) => setDefendantCount(e.target.value)}
          style={{ width: FIELD_W }}
        />
      </Row>

      {/* Location (required) */}
      <SectionTitle>
        Location <span style={{ fontWeight: 400, color: "#6b7280" }}>(required)</span>
      </SectionTitle>

      {isSmall ? (
        <div style={{ margin: "10px 0" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>City *</div>
          <Input
            id="city"
            placeholder="e.g., Los Angeles"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            style={{ width: FIELD_W }}
          />
        </div>
      ) : (
        <Row>
          <FieldLabel id="city" required help="City where the matter is centered." w={LABEL_W}>
            City
          </FieldLabel>
          <Input
            id="city"
            placeholder="e.g., Los Angeles"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            style={{ width: FIELD_W }}
          />
        </Row>
      )}

      {isSmall ? (
        <div style={{ margin: "10px 0" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>County/Metro *</div>
          <Input
            id="county"
            placeholder="e.g., Los Angeles County"
            value={county}
            onChange={(e) => setCounty(e.target.value)}
            style={{ width: FIELD_W }}
          />
        </div>
      ) : (
        <Row>
          <FieldLabel id="county" required help="County or metro area." w={LABEL_W}>
            County/Metro
          </FieldLabel>
          <Input
            id="county"
            placeholder="e.g., Los Angeles County"
            value={county}
            onChange={(e) => setCounty(e.target.value)}
            style={{ width: FIELD_W }}
          />
        </Row>
      )}

      {isSmall ? (
        <div style={{ margin: "10px 0" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>State/Province *</div>
          <Input
            id="state"
            placeholder="e.g., CA"
            value={stateVal}
            onChange={(e) => setStateVal(e.target.value.toUpperCase().slice(0, 40))}
            style={{ width: FIELD_W }}
          />
        </div>
      ) : (
        <Row>
          <FieldLabel id="state" required help="Two-letter for US; freeform elsewhere." w={LABEL_W}>
            State/Province
          </FieldLabel>
          <Input
            id="state"
            placeholder="e.g., CA"
            value={stateVal}
            onChange={(e) => setStateVal(e.target.value.toUpperCase().slice(0, 40))}
            style={{ width: FIELD_W }}
          />
        </Row>
      )}

      {isSmall ? (
        <div style={{ margin: "10px 0" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>ZIP/Postal Code *</div>
          <Input
            id="postalCode"
            placeholder="e.g., 90001"
            value={postal_code}
            onChange={(e) => setPostalCode(e.target.value)}
            style={{ width: FIELD_W }}
          />
        </div>
      ) : (
        <Row>
          <FieldLabel id="postalCode" required help="ZIP/Postal code (global formats allowed)." w={LABEL_W}>
            ZIP/Postal Code
          </FieldLabel>
          <Input
            id="postalCode"
            placeholder="e.g., 90001"
            value={postal_code}
            onChange={(e) => setPostalCode(e.target.value)}
            style={{ width: FIELD_W }}
          />
        </Row>
      )}

      {isSmall ? (
        <div style={{ margin: "10px 0" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Country *</div>
          <Input
            id="country"
            placeholder="e.g., USA"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            style={{ width: FIELD_W }}
          />
        </div>
      ) : (
        <Row>
          <FieldLabel id="country" required help="Country." w={LABEL_W}>
            Country
          </FieldLabel>
          <Input
            id="country"
            placeholder="e.g., USA"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            style={{ width: FIELD_W }}
          />
        </Row>
      )}

      {/* Damages & Insurance */}
      <SectionTitle>Damages & Insurance </SectionTitle>

      <Row wrap>
        <FieldLabel id="insuranceKnown" help="Do you know of any applicable insurance policy limits?">
          Insurance Known
        </FieldLabel>
        <Select id="insuranceKnown" value={insurance_known} onChange={(e) => setInsuranceKnown(e.target.value)}>
          {YES_NO_UNK.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </Select>
      </Row>

      <Row wrap>
        <FieldLabel id="policyLimit" help="If known, per-occurrence policy limit in USD.">
          Policy Limit ($)
        </FieldLabel>
        <Input
          id="policyLimit"
          type="number"
          min="0"
          step="1000"
          placeholder="e.g., 100000"
          value={policy_limit}
          onChange={(e) => setPolicyLimit(e.target.value)}
          style={{ width: FIELD_W }}
        />
      </Row>

      <Row wrap>
        <FieldLabel id="priorOffer" help="Any prior written settlement offer you’ve received.">
          Prior Offer ($)
        </FieldLabel>
        <Input
          id="priorOffer"
          type="number"
          min="0"
          step="500"
          placeholder="e.g., 25000"
          value={prior_offer}
          onChange={(e) => setPriorOffer(e.target.value)}
          style={{ width: FIELD_W }}
        />
      </Row>

      <Row wrap>
        <FieldLabel id="nonEcon" help="Overall pain/suffering or similar non-economic impact.">
          Non-Economic Severity
        </FieldLabel>
        <Select id="nonEcon" value={non_econ_severity} onChange={(e) => setNonEconSeverity(e.target.value)}>
          <option value="">Select…</option>
          {NON_ECON_SCALE.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </Select>
      </Row>

      <Row wrap>
        <FieldLabel id="evidence" help="Strength of evidence supporting your claim.">
          Evidence Strength
        </FieldLabel>
        <Select id="evidence" value={evidence_strength} onChange={(e) => setEvidenceStrength(e.target.value)}>
          <option value="">Select…</option>
          {EVIDENCE_SCALE.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </Select>
      </Row>

      {/* Advanced */}
      <div style={{ marginTop: 10 }}>
        <button
          onClick={() => setShowAdvanced((v) => !v)}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #93c5fd",
            background: "#eef2ff",
            color: "#1d4ed8",
            fontWeight: 700,
            cursor: "pointer",
          }}
          type="button"
        >
          {showAdvanced ? "Hide advanced details" : "Add advanced details (optional)"}
        </button>
      </div>

      {showAdvanced && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            background: "#fafafa",
            overflow: "visible",
          }}
        >
          <Row wrap>
            <FieldLabel id="medBills" help="Medical bills already incurred (gross billed or paid—either is OK).">
              Medical Bills to Date ($)
            </FieldLabel>
            <Input
              id="medBills"
              type="number"
              min="0"
              step="100"
              placeholder="e.g., 12000"
              value={medical_bills_to_date}
              onChange={(e) => setMedicalBillsToDate(e.target.value)}
              style={{ width: FIELD_W }}
            />
          </Row>

          <Row wrap>
            <FieldLabel id="propDamage" help="Property loss/damage (e.g., vehicle repair).">
              Property Damage ($)
            </FieldLabel>
            <Input
              id="propDamage"
              type="number"
              min="0"
              step="100"
              placeholder="e.g., 4500"
              value={property_damage}
              onChange={(e) => setPropertyDamage(e.target.value)}
              style={{ width: FIELD_W }}
            />
          </Row>

          <Row wrap>
            <FieldLabel id="wageLoss" help="Lost wages to date (before taxes).">
              Wage Loss to Date ($)
            </FieldLabel>
            <Input
              id="wageLoss"
              type="number"
              min="0"
              step="100"
              placeholder="e.g., 8000"
              value={wage_loss_to_date}
              onChange={(e) => setWageLossToDate(e.target.value)}
              style={{ width: FIELD_W }}
            />
          </Row>

          <Row wrap>
            <FieldLabel id="futureMed" help="Projected future medical costs (if any).">
              Future Medical ($)
            </FieldLabel>
            <Input
              id="futureMed"
              type="number"
              min="0"
              step="500"
              placeholder="e.g., 20000"
              value={future_medical_estimate}
              onChange={(e) => setFutureMedicalEstimate(e.target.value)}
              style={{ width: FIELD_W }}
            />
          </Row>

          <Row wrap>
            <FieldLabel id="futureWage" help="Projected future lost earning capacity.">
              Future Wage Loss ($)
            </FieldLabel>
            <Input
              id="futureWage"
              type="number"
              min="0"
              step="500"
              placeholder="e.g., 15000"
              value={future_wage_loss}
              onChange={(e) => setFutureWageLoss(e.target.value)}
              style={{ width: FIELD_W }}
            />
          </Row>

          <Row wrap>
            <FieldLabel id="punitiveEligible" help="Punitive damages may apply for malice, fraud, or oppression (varies by jurisdiction).">
              Punitive Eligible
            </FieldLabel>
            <Select id="punitiveEligible" value={punitive_eligible} onChange={(e) => setPunitiveEligible(e.target.value)}>
              {YES_NO_UNK.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </Select>
          </Row>

          <Row wrap>
            <FieldLabel id="publicEntity" help="Public entities can have special notice requirements and damages caps.">
              Public Entity Defendant
            </FieldLabel>
            <Select id="publicEntity" value={public_entity_defendant} onChange={(e) => setPublicEntityDefendant(e.target.value)}>
              {["No", "Yes"].map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </Select>
          </Row>
        </div>
      )}

      {/* Key facts (bounded, resizable) */}
      <SectionTitle>Key Facts & Details</SectionTitle>
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: 12,
          background: "#fafafa",
          overflow: "visible",
        }}
      >
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
          Briefly describe key facts, goals, or constraints (1000 characters max).
        </div>
        <Textarea
          rows={6}
          placeholder="e.g., Imaging shows herniation; missed 6 weeks of work; policy limits uncertain; prior offer $25k."
          value={key_details}
          onChange={(e) => setKeyDetails(clampLen(e.target.value, 1000))}
          spellCheck={true}
        />
      </div>

      {/* Summary Language — full width to match textarea */}
      <div style={{ marginTop: 14, marginBottom: 6, fontWeight: 700 }}>Summary Language</div>
      <div style={{ width: "100%" }}>
        <DocLanguageSelect value={language} onChange={(val) => setLanguage(val)} defaultShowExtended={false} />
      </div>

      {/* Consent */}
      <div style={{ marginTop: 12 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
          <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
          <span>
            I agree to the{" "}
            <a href="/terms" target="_blank" rel="noreferrer">
              Terms of Use
            </a>{" "}
            and{" "}
            <a href="/privacy" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
            .
          </span>
        </label>
      </div>

      {/* Action bar */}
      <div style={{ display: "flex", justifyContent: "flex-start", gap: 8, marginTop: 18 }}>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!coreValid || isGenerating}
          style={{
            padding: "10px 16px",
            border: "none",
            borderRadius: 8,
            background: coreValid && !isGenerating ? "#0ea5e9" : "#94a3b8",
            color: "#fff",
            fontWeight: 700,
            cursor: coreValid && !isGenerating ? "pointer" : "not-allowed",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {isGenerating ? (
            <>
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  border: "2px solid rgba(255,255,255,0.6)",
                  borderTopColor: "transparent",
                  animation: "cve-spin 1s linear infinite",
                  display: "inline-block",
                }}
              />
              Generating…
            </>
          ) : (
            "Generate Estimate"
          )}
        </button>
        <button
          type="button"
          onClick={handleReset}
          style={{
            padding: "10px 14px",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            background: "#fff",
            cursor: "pointer",
          }}
        >
          Reset
        </button>
      </div>

      {/* Logged-in hint */}
      {loggedIn ? <Help style={{ marginTop: 8 }}>You’re signed in — usage limits are not applied.</Help> : null}

      {/* Errors */}
      {error && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            border: "1px solid #fecaca",
            background: "#fef2f2",
            color: "#7f1d1d",
            borderRadius: 10,
          }}
        >
          {error}
        </div>
      )}

      {/* Result */}
      <div ref={resultRef} />
      {result && (
        <div
          style={{
            marginTop: 24,
            padding: 16,
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            background: "#f9fafb",
          }}
        >
          <h3 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700 }}>Estimated Case Value</h3>

          {isSmall ? (
            // Mobile-first: stack vertically to avoid wrap
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>Economic Damages</div>
                <div>
                  {currency(result.economic_range?.min)} – {currency(result.economic_range?.max)}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>Non-Economic</div>
                <div>
                  {currency(result.noneconomic_range?.min)} – {currency(result.noneconomic_range?.max)}
                </div>
              </div>

              {result.punitive_range && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div>Punitive (if applicable)</div>
                  <div>
                    {currency(result.punitive_range?.min)} – {currency(result.punitive_range?.max)}
                  </div>
                </div>
              )}

              {typeof result.policy_limit_applied === "boolean" && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div>Policy Limits Applied</div>
                  <div>{result.policy_limit_applied ? "Yes" : "No"}</div>
                </div>
              )}

              <div style={{ fontWeight: 700, borderTop: "1px dashed #e5e7eb", paddingTop: 8 }}>Estimated Total</div>
              <div style={{ fontWeight: 800 }}>
                {currency(result.total_range?.min)} – {currency(result.total_range?.max)}
              </div>

              <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between" }}>
                <div>Confidence</div>
                <div>{result.confidence}</div>
              </div>
            </div>
          ) : (
            // Desktop/tablet grid (two columns)
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div>Economic Damages</div>
              <div style={{ textAlign: "right" }}>
                {currency(result.economic_range?.min)} – {currency(result.economic_range?.max)}
              </div>

              <div>Non-Economic</div>
              <div style={{ textAlign: "right" }}>
                {currency(result.noneconomic_range?.min)} – {currency(result.noneconomic_range?.max)}
              </div>

              {result.punitive_range && (
                <>
                  <div>Punitive (if applicable)</div>
                  <div style={{ textAlign: "right" }}>
                    {currency(result.punitive_range?.min)} – {currency(result.punitive_range?.max)}
                  </div>
                </>
              )}

              {typeof result.policy_limit_applied === "boolean" && (
                <>
                  <div>Policy Limits Applied</div>
                  <div style={{ textAlign: "right" }}>{result.policy_limit_applied ? "Yes" : "No"}</div>
                </>
              )}

              <div style={{ fontWeight: 700, borderTop: "1px dashed #e5e7eb", paddingTop: 8 }}>Estimated Total</div>
              <div style={{ textAlign: "right", fontWeight: 800, borderTop: "1px dashed #e5e7eb", paddingTop: 8 }}>
                {currency(result.total_range?.min)} – {currency(result.total_range?.max)}
              </div>

              <div style={{ marginTop: 8 }}>Confidence</div>
              <div style={{ textAlign: "right", marginTop: 8 }}>{result.confidence}</div>
            </div>
          )}

          {/* Assumptions */}
          {Array.isArray(result.assumptions) && result.assumptions.length > 0 && (
            <>
              <SectionTitle>Assumptions</SectionTitle>
              <ul style={{ marginTop: 0 }}>
                {result.assumptions.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </>
          )}

          {/* Summary (localized, short; displayed if provided by backend) */}
          {result.summary && (
            <>
              <SectionTitle>Summary</SectionTitle>
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  background: "#ffffff",
                  borderRadius: 10,
                  padding: 12,
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.45,
                }}
              >
                {result.summary}
              </div>
            </>
          )}

          <Help>{result.disclaimer}</Help>
        </div>
      )}

      {/* Spinner keyframes */}
      <style>{"@keyframes cve-spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}"}</style>
    </div>
  );
}
